import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { RotateCcw, Save, Settings } from 'lucide-react'
import {
  isSafeHttpUrl,
  logoFileError,
  SYSTEM_BRANDING_PUBLIC_KEY,
  systemBrandingApi,
  type SystemBrandingAdmin,
} from '@/services/api/systemBranding'
import { Button, LoadingSpinner, Input } from '@/components/common'
import { useToast } from '@/components/ui/ToastProvider'
import { BillingSettingsSection } from '@/components/ultraadmin/BillingSettingsSection'
import { TrimmedLogo } from '@/components/layout/TrimmedLogo'
import { ConfigEditor, type ConfigEditorHandle } from '@/components/config/ConfigEditor'
import { ultraadminApi } from '@/services/api/ultraadmin'
import { extractErrorMessage } from '@/utils/errorHandler'

type LogoSlot = 'email' | 'sidebar'
type NavId = 'nachrichten' | 'dateien' | 'logos' | 'zahlung' | 'hilfe-bot'

const NAV: { id: NavId; subgroup?: string; label: string }[] = [
  { id: 'nachrichten', subgroup: 'Nachrichten', label: 'Wie lange dürfen Nachrichten höchstens bleiben?' },
  { id: 'dateien', subgroup: 'Dateien', label: 'Welche Maximalgrösse und -frist gelten für Dateien?' },
  { id: 'logos', label: 'Welche Logos erscheinen?' },
  { id: 'zahlung', label: 'Wie wird die Plattform verrechnet?' },
  { id: 'hilfe-bot', label: 'Wie spricht Talk?' },
]

export function UltraadminSettingsPage() {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const editorRef = useRef<ConfigEditorHandle>(null)
  const [active, setActive] = useState<NavId>('nachrichten')
  const [dirty, setDirty] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)
  const [configSuccess, setConfigSuccess] = useState(false)
  const successTimeoutRef = useRef<number | null>(null)
  const emailFileRef = useRef<HTMLInputElement>(null)
  const sidebarFileRef = useRef<HTMLInputElement>(null)
  const [data, setData] = useState<SystemBrandingAdmin | null>(null)
  const [url, setUrl] = useState('')
  const [emailFile, setEmailFile] = useState<File | null>(null)
  const [sidebarFile, setSidebarFile] = useState<File | null>(null)
  const [emailPreview, setEmailPreview] = useState<string | null>(null)
  const [sidebarPreview, setSidebarPreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const load = () => {
    setLoading(true)
    setLoadError(false)
    systemBrandingApi
      .get()
      .then((res) => {
        setData(res)
        setUrl(res.powered_by_url || '')
      })
      .catch((err) => {
        setLoadError(true)
        showToast({ message: err?.detail || 'Einstellungen konnten nicht geladen werden.', type: 'error', duration: 4000 })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (window.location.hash === '#zahlung') setActive('zahlung')
  }, [])

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current)
    }
  }, [])

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    setDirty(isDirty)
  }, [])

  const handleConfigError = useCallback((error: string | null) => {
    setConfigError(error)
  }, [])

  const handleConfigSuccess = useCallback(() => {
    setConfigSuccess(true)
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current)
    successTimeoutRef.current = setTimeout(() => setConfigSuccess(false), 5000)
  }, [])

  const handleSaveConfig = async () => {
    if (!editorRef.current?.isDirty()) return
    setSavingConfig(true)
    await editorRef.current.save()
    setSavingConfig(false)
  }

  useEffect(() => {
    return () => {
      if (emailPreview?.startsWith('blob:')) URL.revokeObjectURL(emailPreview)
    }
  }, [emailPreview])

  useEffect(() => {
    return () => {
      if (sidebarPreview?.startsWith('blob:')) URL.revokeObjectURL(sidebarPreview)
    }
  }, [sidebarPreview])

  const onFile = (slot: LogoSlot, chosen: File | null, input: HTMLInputElement | null) => {
    if (chosen) {
      const error = logoFileError(chosen)
      if (error) {
        showToast({ message: error, type: 'error', duration: 5000 })
        if (input) input.value = ''
        return
      }
    }
    if (slot === 'email') {
      setEmailFile(chosen)
      setEmailPreview(chosen ? URL.createObjectURL(chosen) : null)
    } else {
      setSidebarFile(chosen)
      setSidebarPreview(chosen ? URL.createObjectURL(chosen) : null)
    }
  }

  const discardFile = (slot: LogoSlot) => {
    if (slot === 'email') {
      setEmailFile(null)
      setEmailPreview(null)
      if (emailFileRef.current) emailFileRef.current.value = ''
    } else {
      setSidebarFile(null)
      setSidebarPreview(null)
      if (sidebarFileRef.current) sidebarFileRef.current.value = ''
    }
  }

  const afterSave = async (res: SystemBrandingAdmin, message: string, slot?: LogoSlot) => {
    setData(res)
    setUrl(res.powered_by_url || '')
    if (slot) discardFile(slot)
    await queryClient.invalidateQueries({ queryKey: SYSTEM_BRANDING_PUBLIC_KEY })
    showToast({ message, type: 'success', duration: 3000 })
  }

  const saveLogo = async (slot: LogoSlot) => {
    const file = slot === 'email' ? emailFile : sidebarFile
    if (!file) return
    setSaving(true)
    try {
      const res = await systemBrandingApi.update(
        slot === 'email' ? { emailFile: file } : { sidebarFile: file },
      )
      await afterSave(res, slot === 'email' ? 'E-Mail-Logo gespeichert.' : 'Sidebar-Logo gespeichert.', slot)
    } catch (err: unknown) {
      const detail = (err as { detail?: string })?.detail || 'Speichern fehlgeschlagen.'
      showToast({ message: String(detail), type: 'error', duration: 5000 })
    } finally {
      setSaving(false)
    }
  }

  const saveUrl = async () => {
    const trimmed = url.trim()
    if (trimmed && !isSafeHttpUrl(trimmed)) {
      showToast({ message: 'URL muss eine absolute http(s)-Adresse sein.', type: 'error', duration: 4000 })
      return
    }
    setSaving(true)
    try {
      const res = await systemBrandingApi.update({ poweredByUrl: trimmed })
      await afterSave(res, 'Einstellungen gespeichert.')
    } catch (err: unknown) {
      const detail = (err as { detail?: string })?.detail || 'Speichern fehlgeschlagen.'
      showToast({ message: String(detail), type: 'error', duration: 5000 })
    } finally {
      setSaving(false)
    }
  }

  const removeLogo = async (slot: LogoSlot) => {
    setSaving(true)
    try {
      const res = slot === 'email'
        ? await systemBrandingApi.removeEmailLogo()
        : await systemBrandingApi.removeSidebarLogo()
      await afterSave(res, 'Logo entfernt.', slot)
    } catch (err: unknown) {
      const detail = (err as { detail?: string })?.detail || 'Logo konnte nicht entfernt werden.'
      showToast({ message: String(detail), type: 'error', duration: 5000 })
    } finally {
      setSaving(false)
    }
  }

  const pageHeader = (
    <div className="px-4 lg:px-6 py-3 border-b bg-white flex items-center justify-between flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
          <Settings className="w-5 h-5 text-indigo-600" />
        </div>
        <h1 className="text-lg font-bold">Einstellungen</h1>
      </div>
      <div className="flex items-center gap-2">
        {dirty && (
          <button onClick={() => editorRef.current?.reset()} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1">
            <RotateCcw className="w-3.5 h-3.5" /> Verwerfen
          </button>
        )}
        <button onClick={() => void handleSaveConfig()} disabled={savingConfig || !dirty}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50">
          <Save className="w-4 h-4" /> {savingConfig ? 'Speichern...' : 'Speichern'}
        </button>
      </div>
    </div>
  )

  const pageIntro = (
    <div className="px-4 lg:px-6 py-3 bg-white border-b">
      <p className="text-sm text-gray-600">
        Systemweite Vorgaben. Superadmins setzen Löschfristen und Dateigrössen pro Schule und dürfen die hier gesetzten Maxima nicht überschreiten.
        Logos (max. 300 KB, PNG/JPEG/SVG) und Zahlung gelten für die ganze Plattform.
      </p>
    </div>
  )

  const urlError = url.trim() && !isSafeHttpUrl(url) ? 'URL muss eine absolute http(s)-Adresse sein.' : undefined
  const emailOriginal = emailPreview || data?.email_logo_original_data || null
  const sidebarOriginal = sidebarPreview || data?.sidebar_logo_original_data || null
  const configSubgroup = NAV.find(n => n.id === active)?.subgroup
  const showLogos = active === 'logos'
  const showZahlung = active === 'zahlung'
  const showHilfeBot = active === 'hilfe-bot'



  const logosBlock = !loading && !loadError && (
    <>
      <LogoCard
        title="Welches Logo erscheint in E-Mails?"
        description="Erscheint im Footer jeder E-Mail, auf der allgemeinen Login-Seite und auf der Login-Jumppage. SVG wird für E-Mails automatisch als PNG gerastert (E-Mail-Clients blockieren SVG). Dateien nahe am Limit können in E-Mails weggelassen werden (Data-URIs über 300'000 Zeichen)."
        fileRef={emailFileRef}
        file={emailFile}
        hasLogo={!!data?.has_email_logo}
        saving={saving}
        originalSrc={emailOriginal}
        extraPreview={
          <PreviewBox label="E-Mail-Variante">
            {emailOriginal ? (
              <TrimmedLogo src={emailOriginal} alt="E-Mail-Logo Vorschau" variant="light" className="h-12 max-w-[180px] object-contain border border-gray-200 rounded bg-white p-1" />
            ) : (
              <EmptyPreview />
            )}
          </PreviewBox>
        }
        onPick={(file) => onFile('email', file, emailFileRef.current)}
        onDiscard={() => discardFile('email')}
        onSave={() => void saveLogo('email')}
        onRemove={() => void removeLogo('email')}
      />
      <LogoCard
        title="Welches Logo erscheint in der Sidebar?"
        description="Erscheint rechts von «Abmelden» («Powered by»). SVG bleibt scharf. Die Sidebar ist dunkel — Logo auf dunklem Hintergrund prüfen."
        fileRef={sidebarFileRef}
        file={sidebarFile}
        hasLogo={!!data?.has_sidebar_logo}
        saving={saving}
        originalSrc={sidebarOriginal}
        extraPreview={
          <PreviewBox label="Sidebar-Vorschau">
            <div className="bg-slate-900 rounded w-56 flex items-stretch">
              <span className="flex-1 px-4 py-4 text-base font-medium text-slate-300">Abmelden</span>
              <span className="flex w-1/2 flex-col items-stretch justify-center gap-0.5 px-1.5 py-1">
                <span className="text-[11px] text-slate-400 whitespace-nowrap">Powered by</span>
                {sidebarOriginal ? (
                  <TrimmedLogo src={sidebarOriginal} alt="Sidebar-Logo Vorschau" className="block w-full h-auto" />
                ) : (
                  <span className="text-[11px] text-slate-600">—</span>
                )}
              </span>
            </div>
          </PreviewBox>
        }
        onPick={(file) => onFile('sidebar', file, sidebarFileRef.current)}
        onDiscard={() => discardFile('sidebar')}
        onSave={() => void saveLogo('sidebar')}
        onRemove={() => void removeLogo('sidebar')}
      />
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-900">Wohin führen die Logos?</h2>
        <p className="text-sm text-gray-500 mt-1">
          Standard ist https://stitufy.ch (neuer Tab). Eine gesetzte URL überschreibt das für App, Login und E-Mails. Leer lassen behält den Standard.
        </p>
        <div className="mt-4 space-y-3">
          <Input
            label="Ziel-URL"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://stitufy.ch"
            error={urlError}
          />
          <Button type="button" onClick={() => void saveUrl()} disabled={saving || !!urlError}>
            {saving ? 'Speichert…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </>
  )

  return (
    <div className="flex flex-col min-h-full">
      {pageHeader}
      {pageIntro}
      {configError && <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700">{configError}</div>}
      {configSuccess && <div className="bg-green-50 px-4 lg:px-6 py-3 text-sm text-green-700">Konfiguration gespeichert</div>}
      {loadError && (
        <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700 flex items-center gap-3">
          Logos konnten nicht geladen werden.
          <Button type="button" onClick={load}>Erneut versuchen</Button>
        </div>
      )}
      <div className="lg:hidden px-4 py-2 border-b bg-white flex flex-wrap gap-1">
        {NAV.map(item => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActive(item.id)}
            className={`px-3 py-1.5 rounded-md text-sm ${active === item.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="flex flex-1 min-h-0">
        <aside className="hidden lg:block w-72 border-r bg-gray-50/60 flex-shrink-0 overflow-y-auto p-3 space-y-0.5">
          {NAV.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(item.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${active === item.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {item.label}
            </button>
          ))}
        </aside>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className={configSubgroup ? '' : 'hidden'}>
            <ConfigEditor
              ref={editorRef}
              configType="dm"
              hideDelegation
              hideChrome
              forcedSubgroup={configSubgroup}
              onDirtyChange={handleDirtyChange}
              onError={handleConfigError}
              onSuccess={handleConfigSuccess}
            />
          </div>
          {showLogos && (loading ? <div className="p-6 text-gray-500"><LoadingSpinner /></div> : <div className="max-w-3xl mx-auto">{logosBlock}</div>)}
          {showZahlung && (
            <div className="max-w-3xl mx-auto">
              <BillingSettingsSection embedded />
            </div>
          )}
          {showHilfeBot && (
            <TalkSettingsCard />
          )}
        </main>
      </div>
    </div>
  )
}

function EmptyPreview() {
  return (
    <div className="h-12 w-32 border border-dashed border-gray-300 rounded text-xs text-gray-400 flex items-center justify-center">
      —
    </div>
  )
}

function PreviewBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

function LogoCard({
  title,
  description,
  fileRef,
  file,
  hasLogo,
  saving,
  originalSrc,
  extraPreview,
  onPick,
  onDiscard,
  onSave,
  onRemove,
}: {
  title: string
  description: string
  fileRef: RefObject<HTMLInputElement>
  file: File | null
  hasLogo: boolean
  saving: boolean
  originalSrc: string | null
  extraPreview: ReactNode
  onPick: (file: File | null) => void
  onDiscard: () => void
  onSave: () => void
  onRemove: () => void
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="space-y-5">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500">{description}</p>
        <div className="flex flex-wrap gap-6">
          <PreviewBox label="Original">
            {originalSrc ? (
              <img src={originalSrc} alt={`${title} Original`} className="h-12 max-w-[180px] object-contain border border-gray-200 rounded bg-white p-1" />
            ) : (
              <div className="h-12 w-32 border border-dashed border-gray-300 rounded text-xs text-gray-400 flex items-center justify-center">
                Kein Logo
              </div>
            )}
          </PreviewBox>
          {extraPreview}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          className="hidden"
          aria-label={`${title} wählen`}
          onChange={(e) => onPick(e.target.files?.[0] || null)}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => fileRef.current?.click()} disabled={saving}>
            Logo wählen
          </Button>
          {hasLogo && (
            <Button type="button" variant="secondary" onClick={onRemove} disabled={saving}>
              Logo entfernen
            </Button>
          )}
          {file && (
            <>
              <span className="text-sm text-amber-700 self-center">Noch nicht gespeichert: {file.name}</span>
              <Button type="button" variant="ghost" onClick={onDiscard} disabled={saving}>
                Auswahl verwerfen
              </Button>
              <Button type="button" onClick={onSave} disabled={saving}>
                {saving ? 'Speichert…' : 'Speichern'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function TalkSettingsCard() {
  const { showToast } = useToast()
  const [prompt, setPrompt] = useState('')
  const [voice, setVoice] = useState('{"de":"eve","fr":"ara","en":"rex"}')
  const [turns, setTurns] = useState(30)
  const [stt, setStt] = useState(600)
  const [tts, setTts] = useState(20000)
  const [model, setModel] = useState('')
  const [banner, setBanner] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    void ultraadminApi.getTalk().then((row) => {
      setPrompt(row.system_prompt || '')
      setVoice(JSON.stringify(row.voice_map || {}, null, 0))
      setTurns(row.limit_turns)
      setStt(row.limit_stt_s)
      setTts(row.limit_tts_chars)
      setModel(row.active_model || '')
      setBanner(row.rollback_banner)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])
  async function save(clearBanner = false) {
    setSaving(true)
    try {
      let map = {}
      try { map = JSON.parse(voice) } catch { map = {} }
      await ultraadminApi.putTalk({
        system_prompt: prompt,
        voice_map: map,
        limit_turns: turns,
        limit_stt_s: stt,
        limit_tts_chars: tts,
        clear_banner: clearBanner,
      })
      if (clearBanner) setBanner(null)
      showToast({ message: 'Talk-Settings gespeichert.', type: 'success', duration: 2500 })
    } catch (e) {
      showToast({ message: extractErrorMessage(e, { loadError: 'Talk-Settings fehlgeschlagen.' }), type: 'error', duration: 4000 })
    } finally {
      setSaving(false)
    }
  }
  if (loading) return <div className="max-w-3xl mx-auto p-6 text-gray-500"><LoadingSpinner /></div>
  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Talk</h2>
        <p className="text-sm text-gray-500">Eine Vorlage für alle Schulen. Modell {model || '—'}</p>
        {banner ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
            {banner}
            <Button type="button" variant="secondary" className="ml-2" onClick={() => void save(true)}>ok</Button>
          </div>
        ) : null}
        <label className="block text-sm text-gray-700">Systemprompt<textarea className="mt-1 w-full min-h-32 text-sm border rounded p-2" value={prompt} onChange={e => setPrompt(e.target.value)} /></label>
        <label className="block text-sm text-gray-700">Stimmen-Map JSON<input className="mt-1 w-full text-sm border rounded p-2" value={voice} onChange={e => setVoice(e.target.value)} /></label>
        <label className="block text-sm">Turns/h <input type="number" className="border rounded p-1 ml-2" value={turns} onChange={e => setTurns(Number(e.target.value))} /></label>
        <label className="block text-sm">STT-s/h <input type="number" className="border rounded p-1 ml-2" value={stt} onChange={e => setStt(Number(e.target.value))} /></label>
        <label className="block text-sm">TTS-Zeichen/h <input type="number" className="border rounded p-1 ml-2" value={tts} onChange={e => setTts(Number(e.target.value))} /></label>
        <Button type="button" onClick={() => void save()} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Button>
      </div>
    </div>
  )
}
